import React from 'react';
import { useTranslation } from 'react-i18next';
import { makeStyles, Tooltip } from '@material-ui/core';
import { Button } from '@avangard/ui/core';
import { white, black } from '@avangard/ui/colors';

import { CommonHelper } from '@helpers';

import type { TestEntity, ActiveTestEntity } from '@modules/testing/entities';

type ButtonStartSubjectTestProps = {
    test: TestEntity;
    activeTest: ActiveTestEntity;
    subjectId: string;
    onStart: () => void;
    disabled?: boolean;
};

const useTooltipStyles = makeStyles({
    tooltip: {
        maxWidth: 400,
        padding: '8px 16px',
        fontSize: '1.3rem',
        color: black[70],
        backgroundColor: white[100],
        borderRadius: 4,
        boxShadow: `3px 1px 8px ${black[10]}`,
    },
    arrow: {
        color: white[100],
    },
});

const ButtonStartSubjectTest = (props: ButtonStartSubjectTestProps): React.ReactElement => {
    const { test, activeTest, subjectId, disabled, onStart } = props;

    const { t } = useTranslation(['common', 'testing']);

    const tooltipClasses = useTooltipStyles();

    if (test.finished && test.resultSuccess) {
        const points = CommonHelper.createCountFormatter(
            test.resultScore,
            [
                t('common:blocks.points.one'),
                t('common:blocks.points.some'),
                t('common:blocks.points.multiple'),
            ],
            { decimal: 2 },
        );

        return (
            <Tooltip
                arrow
                placement='left'
                title={
                    t('testing:blocks.titles.finished_test', {
                        points,
                        usualScore: test.usualScore,
                    }) ?? ''
                }
                classes={tooltipClasses}
            >
                <div>
                    <Button fullWidth disabled>
                        {t('testing:actions.passed_test')}
                    </Button>
                </div>
            </Tooltip>
        );
    }

    if (activeTest.exists && activeTest.subjectId !== subjectId) {
        return (
            <Tooltip
                arrow
                placement='left'
                title={
                    t('testing:blocks.titles.already_passing_test', { name: activeTest.name }) ?? ''
                }
                classes={tooltipClasses}
            >
                <div>
                    <Button fullWidth disabled>
                        {t('testing:actions.get_tested')}
                    </Button>
                </div>
            </Tooltip>
        );
    }

    return (
        <Button disabled={disabled} onClick={onStart}>
            {activeTest.exists
                ? t('testing:actions.continue_test')
                : t('testing:actions.get_tested')}
        </Button>
    );
};

export { ButtonStartSubjectTest };
